import { Session } from 'next-auth';
import { apiService } from '@/lib/services/api.service';
import { API_CONFIG } from '@/lib/config/api.config';

// Request deduplication map
const pendingRequests = new Map<string, Promise<any>>();

export interface Subscription {
  plan: 'free' | 'pro';
  status: string;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  stripeCustomerId: string | null;
}

export interface PaymentTransaction {
  id: string;
  amount: number;         // raw cents (e.g. 1500 = $15.00)
  currency: string;       // e.g. "cad", "usd"
  status: string;         // "pending" | "succeeded" | "failed" | "refunded"
  description: string;
  createdAt: string;
  documentId: string | null;
  type: 'subscription' | 'document';
}

export interface PaginatedPaymentHistory {
  data: PaymentTransaction[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

interface CheckoutSession {
  url: string;
}

class PaymentsService {
  private dedupeRequest<T>(key: string, request: Promise<T>): Promise<T> {
    const pending = pendingRequests.get(key);
    if (pending) {
      return pending as Promise<T>;
    }

    pendingRequests.set(key, request);
    return request.finally(() => {
      pendingRequests.delete(key);
    });
  }

  async getSubscription(session: Session) {
    const key = 'subscription';
    return this.dedupeRequest(
      key,
      apiService.get<Subscription>(
        API_CONFIG.ENDPOINTS.PAYMENTS.SUBSCRIPTION,
        session
      )
    );
  }

  async createSubscription(data: { priceId: string }, session: Session) {
    const key = `create-subscription-${JSON.stringify(data)}`;
    return this.dedupeRequest(
      key,
      apiService.post<Subscription>(
        API_CONFIG.ENDPOINTS.PAYMENTS.SUBSCRIPTION,
        data,
        session
      )
    );
  }

  async updateSubscription(data: { priceId: string }, session: Session) {
    const key = `update-subscription-${JSON.stringify(data)}`;
    return this.dedupeRequest(
      key,
      apiService.put<Subscription>(
        API_CONFIG.ENDPOINTS.PAYMENTS.SUBSCRIPTION,
        data,
        session
      )
    );
  }

  async downgradeToFree(session: Session) {
    const key = 'downgrade-to-free';
    return this.dedupeRequest(
      key,
      apiService.delete(API_CONFIG.ENDPOINTS.PAYMENTS.SUBSCRIPTION, session)
    );
  }

  async getPaymentHistory(
    page: number = 1,
    limit: number = 10,
    session: Session
  ): Promise<PaginatedPaymentHistory | null> {
    const key = `payment-history-${page}-${limit}`;
    return this.dedupeRequest(
      key,
      apiService.get<PaginatedPaymentHistory>(
        `${API_CONFIG.ENDPOINTS.PAYMENTS.HISTORY}?page=${page}&limit=${limit}`,
        session
      )
    );
  }

  async createCheckoutSession(priceId: string, session: Session) {
    const key = `checkout-${priceId}`;
    return this.dedupeRequest(
      key,
      apiService.post<CheckoutSession>(
        API_CONFIG.ENDPOINTS.PAYMENTS.CHECKOUT,
        { priceId },
        session
      )
    );
  }
}

export const paymentsService = new PaymentsService();
